import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, CheckCircle, X, AlertCircle, Download, Eye,
  ChevronDown, ChevronRight, ClipboardList, Building2, RefreshCw,
} from 'lucide-react';
import * as documentService from '../../services/documentService';
import { Document } from '../../services/documentService';
import userService from '../../services/userService';
import { User } from '../../services/userService';
import { getUserData } from '../../utils/authUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClinicReports {
  user: User;
  documents: Document[];
}

type DocStatus = 'pendiente' | 'aprobado' | 'rechazado';

interface DocEditState {
  /** Status shown in the dropdown (may differ from doc.status while editing) */
  pendingStatus: DocStatus;
  comments: string;
  saving: boolean;
  /** Timestamp of last successful save, used to show checkmark */
  savedAt: number | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS = [
  { value: 1, label: 'Enero' },
  { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },
  { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' },
  { value: 12, label: 'Diciembre' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => 2020 + i);

// ─── Helper components ────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: DocStatus }> = ({ status }) => {
  if (status === 'aprobado')
    return (
      <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2.5 py-1 rounded-full text-xs font-semibold">
        <CheckCircle size={12} /> Aprobado
      </span>
    );
  if (status === 'rechazado')
    return (
      <span className="inline-flex items-center gap-1 text-red-700 bg-red-100 px-2.5 py-1 rounded-full text-xs font-semibold">
        <X size={12} /> Rechazado
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 text-yellow-700 bg-yellow-100 px-2.5 py-1 rounded-full text-xs font-semibold">
      <AlertCircle size={12} /> Pendiente
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const AdminReportsView: React.FC = () => {
  const adminId = getUserData()?.userId ?? 0;

  const [clinicReports, setClinicReports] = useState<ClinicReports[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Expand state
  const [expandedClinics, setExpandedClinics] = useState<Set<number>>(new Set());
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());

  // Per-document edit state (only created when user interacts)
  const [docEdits, setDocEdits] = useState<Record<number, DocEditState>>({});

  // Filters
  const [filterClinicId, setFilterClinicId] = useState<number | 'all'>('all');
  const [filterYear, setFilterYear] = useState<number | 'all'>('all');
  const [filterMonth, setFilterMonth] = useState<number | 'all'>('all');

  // ─── Data loading ───────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Single call: all client reports across all clinics
      const { documents: allDocs } = await documentService.getAllClientReportsAdmin();

      if (allDocs.length === 0) {
        setClinicReports([]);
        setDocEdits({});
        return;
      }

      // Group documents by userId
      const byUser: Record<number, Document[]> = {};
      allDocs.forEach(doc => {
        (byUser[doc.userId] ??= []).push(doc);
      });

      // Fetch user info for each userId that has documents
      const userIds = Object.keys(byUser).map(Number);
      const userResults = await Promise.allSettled(
        userIds.map(uid => userService.getUserById(uid))
      );

      const withDocs: ClinicReports[] = userResults
        .map((result, i) => {
          const uid = userIds[i];
          const docs = byUser[uid];
          if (result.status === 'fulfilled') {
            return { user: result.value.user, documents: docs };
          }
          // Fallback: create a placeholder user if fetch fails
          const placeholder: User = {
            id: uid,
            username: `Clínica (ID: ${uid})`,
            email: '',
            role: 'USER',
            active: true,
            createdAt: '',
          };
          return { user: placeholder, documents: docs };
        });

      setClinicReports(withDocs);

      // Auto-expand clinics that have pending reports
      const clinicsWithPending = withDocs
        .filter(r => r.documents.some(d => d.status === 'pendiente'))
        .map(r => r.user.id);
      setExpandedClinics(new Set(clinicsWithPending));

      setDocEdits({});
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error al cargar reportes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ─── Expand toggles ─────────────────────────────────────────────────────────

  const toggleClinic = (userId: number) =>
    setExpandedClinics(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });

  const togglePeriod = (key: string) =>
    setExpandedPeriods(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const expandAll = () => {
    setExpandedClinics(new Set(clinicReports.map(c => c.user.id)));
    const allPeriodKeys: string[] = [];
    clinicReports.forEach(({ user, documents }) => {
      const seen = new Set<string>();
      documents.forEach(d => {
        const key = `${user.id}-${d.year}-${String(d.month).padStart(2, '0')}`;
        if (!seen.has(key)) { seen.add(key); allPeriodKeys.push(key); }
      });
    });
    setExpandedPeriods(new Set(allPeriodKeys));
  };

  const collapseAll = () => {
    setExpandedClinics(new Set());
    setExpandedPeriods(new Set());
  };

  const allExpanded = clinicReports.length > 0 &&
    clinicReports.every(c => expandedClinics.has(c.user.id));

  // ─── Edit state helpers ─────────────────────────────────────────────────────

  const getEdit = (doc: Document): DocEditState =>
    docEdits[doc.id] ?? {
      pendingStatus: doc.status,
      comments: '',
      saving: false,
      savedAt: null,
    };

  const patchEdit = (docId: number, patch: Partial<DocEditState>) =>
    setDocEdits(prev => ({
      ...prev,
      [docId]: { ...prev[docId] ?? { pendingStatus: 'pendiente', comments: '', saving: false, savedAt: null }, ...patch },
    }));

  // ─── Update document in local state after save ──────────────────────────────

  const applyDocumentUpdate = (updated: Document) => {
    setClinicReports(prev =>
      prev.map(clinic => ({
        ...clinic,
        documents: clinic.documents.map(d => (d.id === updated.id ? updated : d)),
      }))
    );
  };

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  };

  // ─── Status change handler ──────────────────────────────────────────────────

  const handleStatusChange = async (doc: Document, newStatus: DocStatus) => {
    patchEdit(doc.id, { pendingStatus: newStatus, comments: '' });

    if (newStatus === 'rechazado' || newStatus === 'aprobado') {
      // Don't save immediately — wait for the admin to fill the form and confirm
      return;
    }

    // Only 'pendiente' reverts immediately
    patchEdit(doc.id, { saving: true });
    try {
      const res = await documentService.updateDocumentStatus(doc.id, 'pendiente');
      applyDocumentUpdate(res.document);
      patchEdit(doc.id, { saving: false, savedAt: Date.now(), pendingStatus: 'pendiente' });
      showSuccess(`Estado actualizado a "pendiente" correctamente`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar estado');
      patchEdit(doc.id, { saving: false, pendingStatus: doc.status });
    }
  };

  // ─── Confirm rejection ──────────────────────────────────────────────────────

  const handleConfirmReject = async (doc: Document) => {
    const edit = getEdit(doc);
    if (!edit.comments.trim()) return;

    patchEdit(doc.id, { saving: true });
    try {
      const { document: updated } = await documentService.rejectDocument(
        doc.id,
        adminId,
        edit.comments.trim()
      );
      applyDocumentUpdate(updated);
      patchEdit(doc.id, { saving: false, savedAt: Date.now(), pendingStatus: 'rechazado', comments: '' });
      showSuccess('Reporte rechazado y clínica notificada');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al rechazar documento');
      patchEdit(doc.id, { saving: false });
    }
  };

  const handleCancelReject = (doc: Document) => {
    patchEdit(doc.id, { pendingStatus: doc.status, comments: '' });
  };

  // ─── Confirm approval ───────────────────────────────────────────────────────

  const handleConfirmApprove = async (doc: Document) => {
    const edit = getEdit(doc);
    patchEdit(doc.id, { saving: true });
    try {
      const { document: updated } = await documentService.approveDocument(
        doc.id,
        adminId,
        edit.comments.trim() || undefined
      );
      applyDocumentUpdate(updated);
      patchEdit(doc.id, { saving: false, savedAt: Date.now(), pendingStatus: 'aprobado', comments: '' });
      showSuccess('Reporte aprobado y clínica notificada');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al aprobar documento');
      patchEdit(doc.id, { saving: false });
    }
  };

  // ─── View / Download ────────────────────────────────────────────────────────

  const handleView = async (doc: Document) => {
    try {
      await documentService.viewDocument(doc.id);
    } catch {
      setError('No se pudo abrir el documento. Intenta de nuevo.');
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      await documentService.downloadDocument(doc.id, doc.originalFileName);
    } catch {
      setError('No se pudo descargar el documento. Intenta de nuevo.');
    }
  };

  // ─── Grouping & filtering ───────────────────────────────────────────────────

  const groupByPeriod = (documents: Document[]) => {
    const grouped: Record<string, Document[]> = {};
    documents.forEach(doc => {
      const key = `${doc.year}-${String(doc.month).padStart(2, '0')}`;
      (grouped[key] ??= []).push(doc);
    });
    // Sort newest first
    return Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a));
  };

  const filteredClinics = React.useMemo(() => {
    return clinicReports
    .filter(c => filterClinicId === 'all' || c.user.id === filterClinicId)
    .map(c => ({
      ...c,
      documents: c.documents.filter(d => {
        if (filterYear !== 'all' && d.year !== filterYear) return false;
        if (filterMonth !== 'all' && d.month !== filterMonth) return false;
        return true;
      }),
    }))
    .filter(c => c.documents.length > 0);
  }, [clinicReports, filterClinicId, filterYear, filterMonth]);

  const totalPending = React.useMemo(() => {
    return clinicReports.reduce(
    (sum, c) => sum + c.documents.filter(d => d.status === 'pendiente').length,
    0
  );
}, [clinicReports]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <ClipboardList className="text-red-600" size={28} />
          Reportes de Clínicas
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          {totalPending > 0 && (
            <span className="flex items-center gap-1.5 bg-yellow-100 text-yellow-800 text-sm font-semibold px-3 py-1.5 rounded-full">
              <AlertCircle size={16} />
              {totalPending} pendiente{totalPending !== 1 ? 's' : ''}
            </span>
          )}
          {clinicReports.length > 0 && (
            <button
              onClick={allExpanded ? collapseAll : expandAll}
              className="text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition"
            >
              {allExpanded ? 'Colapsar todo' : 'Expandir todo'}
            </button>
          )}
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg transition disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Alert messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-start gap-2">
          <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={18} />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 border border-green-300 text-green-700 rounded-lg flex items-center gap-2">
          <CheckCircle size={20} className="flex-shrink-0" />
          <span className="flex-1 text-sm">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="text-green-500 hover:text-green-700">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Clínica</label>
          <select
            value={filterClinicId}
            onChange={e =>
              setFilterClinicId(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">Todas las clínicas</option>
            {clinicReports.map(c => (
              <option key={c.user.id} value={c.user.id}>
                {c.user.companyName || c.user.username}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Año</label>
          <select
            value={filterYear}
            onChange={e =>
              setFilterYear(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">Todos los años</option>
            {YEARS.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Mes</label>
          <select
            value={filterMonth}
            onChange={e =>
              setFilterMonth(e.target.value === 'all' ? 'all' : Number(e.target.value))
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">Todos los meses</option>
            {MONTHS.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto" />
          <p className="mt-4 text-gray-500">Cargando reportes de clínicas...</p>
        </div>
      ) : filteredClinics.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText size={52} className="mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium text-gray-500">No hay reportes disponibles</p>
          <p className="text-sm mt-1 text-gray-400">
            {clinicReports.length === 0
              ? 'Ninguna clínica ha subido reportes aún. Verifica que los centros médicos tengan rol USER y hayan subido documentos.'
              : 'No hay reportes que coincidan con los filtros seleccionados.'}
          </p>
          {clinicReports.length === 0 && (
            <button
              onClick={loadData}
              className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Reintentar carga
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClinics.map(({ user, documents }) => {
            const isOpen = expandedClinics.has(user.id);
            const pending = documents.filter(d => d.status === 'pendiente').length;
            const periods = groupByPeriod(documents);
            const clinicName = user.companyName || user.username;

            return (
              <div
                key={user.id}
                className="border border-gray-200 rounded-xl overflow-hidden shadow-sm"
              >
                {/* ── Clinic row ── */}
                <button
                  onClick={() => toggleClinic(user.id)}
                  className="w-full flex items-center justify-between bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white px-5 py-4 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    {isOpen
                      ? <ChevronDown size={20} className="text-slate-300 flex-shrink-0" />
                      : <ChevronRight size={20} className="text-slate-300 flex-shrink-0" />
                    }
                    <Building2 size={22} className="text-slate-300 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-base leading-tight">{clinicName}</p>
                      <p className="text-slate-400 text-xs">
                        {user.rut && `RUT ${user.rut} · `}
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                    <span className="bg-slate-600 text-slate-200 text-xs px-2.5 py-1 rounded-full">
                      {documents.length} reporte{documents.length !== 1 ? 's' : ''}
                    </span>
                    {pending > 0 && (
                      <span className="bg-yellow-400 text-yellow-900 font-bold text-xs px-2.5 py-1 rounded-full">
                        {pending} pendiente{pending !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </button>

                {/* ── Periods inside clinic ── */}
                {isOpen && (
                  <div className="divide-y divide-gray-100">
                    {periods.map(([periodKey, periodDocs]) => {
                      const [year, monthStr] = periodKey.split('-');
                      const month = parseInt(monthStr, 10);
                      const periodLabel = documentService.formatPeriod(Number(year), month);
                      const expandKey = `${user.id}-${periodKey}`;
                      const isPeriodOpen = expandedPeriods.has(expandKey);
                      // Use edit state status if available, else doc.status
                      const periodStatuses = periodDocs.map(d => docEdits[d.id]?.pendingStatus ?? d.status);

                      return (
                        <div key={periodKey}>
                          {/* ── Period header ── */}
                          <button
                            onClick={() => togglePeriod(expandKey)}
                            className="w-full flex items-center justify-between bg-blue-50 hover:bg-blue-100 px-5 py-3 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3">
                              {isPeriodOpen
                                ? <ChevronDown size={17} className="text-blue-600" />
                                : <ChevronRight size={17} className="text-blue-600" />
                              }
                              <span className="font-semibold text-gray-700">{periodLabel}</span>
                              <span className="text-xs text-gray-400 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                                {periodDocs.length} archivo{periodDocs.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {periodStatuses.some(s => s === 'aprobado') && (
                                <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full font-medium">Aprobado</span>
                              )}
                              {periodStatuses.some(s => s === 'rechazado') && (
                                <span className="text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full font-medium">Rechazado</span>
                              )}
                              {periodStatuses.some(s => s === 'pendiente') && (
                                <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full font-medium">Pendiente</span>
                              )}
                            </div>
                          </button>

                          {/* ── Documents table ── */}
                          {isPeriodOpen && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Documento</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Subido</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Tamaño</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Decisión</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {periodDocs.map(doc => {
                                    const edit = getEdit(doc);
                                    const isRejecting =
                                      edit.pendingStatus === 'rechazado' &&
                                      doc.status !== 'rechazado';
                                    const isRejectingExisting =
                                      edit.pendingStatus === 'rechazado' &&
                                      doc.status === 'rechazado';
                                    const showRejectForm = edit.pendingStatus === 'rechazado';
                                    const showApproveForm = edit.pendingStatus === 'aprobado';
                                    const isApproving = edit.pendingStatus === 'aprobado' && doc.status !== 'aprobado';
                                    return (
                                      <React.Fragment key={doc.id}>
                                        <tr className="hover:bg-gray-50 transition-colors">
                                          {/* File name */}
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                              <FileText size={16} className="text-blue-500 flex-shrink-0" />
                                              <div>
                                                <p className="font-medium text-gray-900 leading-tight">
                                                  {doc.originalFileName}
                                                </p>
                                                <p className="text-xs text-gray-400">v{doc.version}</p>
                                              </div>
                                            </div>
                                          </td>
                                          {/* Upload date */}
                                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {new Date(doc.uploadedAt).toLocaleDateString('es-CL', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                            })}
                                          </td>
                                          {/* File size */}
                                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                            {documentService.formatFileSize(doc.fileSize)}
                                          </td>
                                          {/* Current status (from DB) */}
                                          <td className="px-4 py-3">
                                            <StatusBadge status={doc.status} />
                                          </td>
                                          {/* Decision dropdown */}
                                          <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                              <select
                                                value={edit.pendingStatus}
                                                onChange={e =>
                                                  handleStatusChange(doc, e.target.value as DocStatus)
                                                }
                                                disabled={edit.saving}
                                                className={`text-sm border rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-red-400 focus:border-transparent transition disabled:opacity-40 cursor-pointer ${
                                                  edit.pendingStatus === 'aprobado'
                                                    ? 'border-green-300 bg-green-50 text-green-800'
                                                    : edit.pendingStatus === 'rechazado'
                                                    ? 'border-red-300 bg-red-50 text-red-800'
                                                    : 'border-yellow-300 bg-yellow-50 text-yellow-800'
                                                }`}
                                              >
                                                <option value="pendiente">Pendiente</option>
                                                <option value="aprobado">Aprobar</option>
                                                <option value="rechazado">Rechazar</option>
                                              </select>
                                              {edit.saving && (
                                                <div className="h-4 w-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                                              )}
                                              {edit.savedAt && !edit.saving && (
                                                <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                                              )}
                                            </div>
                                          </td>
                                          {/* View / Download */}
                                          <td className="px-4 py-3">
                                            <div className="flex items-center justify-center gap-1">
                                              <button
                                                onClick={() => handleView(doc)}
                                                title="Ver documento"
                                                className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition"
                                              >
                                                <Eye size={18} />
                                              </button>
                                              <button
                                                onClick={() => handleDownload(doc)}
                                                title="Descargar documento"
                                                className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition"
                                              >
                                                <Download size={18} />
                                              </button>
                                            </div>
                                          </td>
                                        </tr>

                                        {/* ── Rejection form row ── */}
                                        {showRejectForm && (
                                          <tr className="bg-red-50">
                                            <td colSpan={6} className="px-5 py-4">
                                              <div className="flex flex-col gap-3 max-w-2xl">
                                                <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5">
                                                  <AlertCircle size={15} />
                                                  {isRejecting
                                                    ? 'Ingresa el motivo del rechazo (obligatorio):'
                                                    : 'Actualizar motivo del rechazo:'}
                                                </p>
                                                <textarea
                                                  value={edit.comments}
                                                  onChange={e =>
                                                    patchEdit(doc.id, { comments: e.target.value })
                                                  }
                                                  placeholder="Describe claramente los motivos para que la clínica pueda corregirlo..."
                                                  rows={3}
                                                  className="w-full text-sm border border-red-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-400 focus:border-transparent resize-none bg-white"
                                                />
                                                <div className="flex items-center gap-2">
                                                  <button
                                                    onClick={() => handleConfirmReject(doc)}
                                                    disabled={edit.saving || !edit.comments.trim()}
                                                    className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm px-4 py-2 rounded-lg transition font-semibold"
                                                  >
                                                    {edit.saving ? (
                                                      <>
                                                        <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        Guardando...
                                                      </>
                                                    ) : (
                                                      <>
                                                        <X size={14} />
                                                        {isRejecting ? 'Confirmar rechazo' : 'Actualizar rechazo'}
                                                      </>
                                                    )}
                                                  </button>
                                                  <button
                                                    onClick={() => handleCancelReject(doc)}
                                                    disabled={edit.saving}
                                                    className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
                                                  >
                                                    Cancelar
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                        {/* ── Approval form row ── */}
                                        {showApproveForm && (
                                          <tr className="bg-green-50">
                                            <td colSpan={6} className="px-5 py-4">
                                              <div className="flex flex-col gap-3 max-w-2xl">
                                                <p className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
                                                  <CheckCircle size={15} />
                                                  {isApproving
                                                    ? 'Observación para la clínica (opcional):'
                                                    : 'Actualizar observación de aprobación:'}
                                                </p>
                                                <textarea
                                                  value={edit.comments}
                                                  onChange={e =>
                                                    patchEdit(doc.id, { comments: e.target.value })
                                                  }
                                                  placeholder="Puedes agregar una nota o comentario para la clínica..."
                                                  rows={3}
                                                  className="w-full text-sm border border-green-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-400 focus:border-transparent resize-none bg-white"
                                                />
                                                <div className="flex items-center gap-2">
                                                  <button
                                                    onClick={() => handleConfirmApprove(doc)}
                                                    disabled={edit.saving}
                                                    className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-sm px-4 py-2 rounded-lg transition font-semibold"
                                                  >
                                                    {edit.saving ? (
                                                      <>
                                                        <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                        Guardando...
                                                      </>
                                                    ) : (
                                                      <>
                                                        <CheckCircle size={14} />
                                                        {isApproving ? 'Confirmar aprobación' : 'Actualizar aprobación'}
                                                      </>
                                                    )}
                                                  </button>
                                                  <button
                                                    onClick={() => handleCancelReject(doc)}
                                                    disabled={edit.saving}
                                                    className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
                                                  >
                                                    Cancelar
                                                  </button>
                                                </div>
                                              </div>
                                            </td>
                                          </tr>
                                        )}

                                        {/* ── Existing rejection reason (read-only) ── */}
                                        {!showRejectForm && doc.status === 'rechazado' && doc.comments && (
                                          <tr className="bg-red-50">
                                            <td colSpan={6} className="px-5 py-2">
                                              <p className="text-xs text-red-700 flex items-start gap-1.5">
                                                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                                                <span>
                                                  <strong>Motivo del rechazo:</strong> {doc.comments}
                                                </span>
                                              </p>
                                            </td>
                                          </tr>
                                        )}

                                        {/* ── Existing approval observation (read-only) ── */}
                                        {!showApproveForm && doc.status === 'aprobado' && doc.comments && (
                                          <tr className="bg-green-50">
                                            <td colSpan={6} className="px-5 py-2">
                                              <p className="text-xs text-green-700 flex items-start gap-1.5">
                                                <CheckCircle size={13} className="mt-0.5 flex-shrink-0" />
                                                <span>
                                                  <strong>Observación:</strong> {doc.comments}
                                                </span>
                                              </p>
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminReportsView;
